/**
 * Defaults and named copies for the asset extraction playground.
 * The numbers start as the values hardcoded in the extractor.
 *
 * @format
 */

import {
	CAPTION_WORD_LIST,
	DEFAULT_FIGURE_CLUSTER_TUNE,
	DEFAULT_WORDING_INSTRUCTIONS,
	DEFAULT_WORDING_LINE,
	DEFAULT_WORDING_TUNE,
	FIGURE_RASTER_SETTINGS,
	type FigureClusterTune,
	type WordingTune,
} from "@question-bank/shared";

export const ASSET_PLAYGROUND_DRAFT_KEY = "assetPlayground.draft";
export const ASSET_PLAYGROUND_VARIATIONS_KEY = "assetPlayground.variations";

export const ASSET_PLAYGROUND_MODELS = ["lite", "flash", "pro"] as const;
export type AssetPlaygroundModel = (typeof ASSET_PLAYGROUND_MODELS)[number];

export interface AssetPlaygroundSettings {
	hairline: number;
	seedShort: number;
	seedLong: number;
	clusterGap: number;
	imageMinPx: number;
	nearMargin: number;
	glyphMaxSide: number;
	glyphStroke: number;
	rasterScale: number;
	rasterPadX: number;
	rasterPadY: number;
	rasterTrimMargin: number;
	rasterTrimReach: number;
	labelBand: number;
	labelBandRight: number;
	shortLabelWords: number;
	wordGap: number;
	lineTolerance: number;
	maxCandidates: number;
	maxPages: number;
	captionWords: string;
	prompt: string;
	lineFormat: string;
	model: AssetPlaygroundModel;
	language: "en" | "cy";
	shouldAskModel: boolean;
}

export interface NamedPlaygroundVariation {
	name: string;
	settings: AssetPlaygroundSettings;
}

type PlaygroundNumberKey = {
	[Key in keyof AssetPlaygroundSettings]: AssetPlaygroundSettings[Key] extends number ? Key : never;
}[keyof AssetPlaygroundSettings];

export const PLAYGROUND_NUMBER_FIELDS: {
	key: PlaygroundNumberKey;
	labelKey: string;
	step: string;
}[] = [
	{ key: "nearMargin", labelKey: "assetPlayground.nearMargin", step: "1" },
	{ key: "labelBand", labelKey: "assetPlayground.labelBand", step: "1" },
	{ key: "labelBandRight", labelKey: "assetPlayground.labelBandRight", step: "1" },
	{ key: "shortLabelWords", labelKey: "assetPlayground.shortLabelWords", step: "1" },
	{ key: "wordGap", labelKey: "assetPlayground.wordGap", step: "0.1" },
	{ key: "lineTolerance", labelKey: "assetPlayground.lineTolerance", step: "0.1" },
	{ key: "maxCandidates", labelKey: "assetPlayground.maxCandidates", step: "1" },
	{ key: "hairline", labelKey: "assetPlayground.hairline", step: "1" },
	{ key: "seedShort", labelKey: "assetPlayground.seedShort", step: "1" },
	{ key: "seedLong", labelKey: "assetPlayground.seedLong", step: "1" },
	{ key: "clusterGap", labelKey: "assetPlayground.clusterGap", step: "1" },
	{ key: "imageMinPx", labelKey: "assetPlayground.imageMinPx", step: "1" },
	{ key: "glyphMaxSide", labelKey: "assetPlayground.glyphMaxSide", step: "1" },
	{ key: "glyphStroke", labelKey: "assetPlayground.glyphStroke", step: "0.1" },
	{ key: "rasterScale", labelKey: "assetPlayground.rasterScale", step: "0.5" },
	{ key: "rasterPadX", labelKey: "assetPlayground.rasterPadX", step: "1" },
	{ key: "rasterPadY", labelKey: "assetPlayground.rasterPadY", step: "1" },
	{ key: "rasterTrimMargin", labelKey: "assetPlayground.rasterTrimMargin", step: "1" },
	{ key: "rasterTrimReach", labelKey: "assetPlayground.rasterTrimReach", step: "1" },
	{ key: "maxPages", labelKey: "assetPlayground.maxPages", step: "1" },
];

export const defaultPlaygroundSettings = (): AssetPlaygroundSettings => ({
	hairline: DEFAULT_FIGURE_CLUSTER_TUNE.hairline,
	seedShort: DEFAULT_FIGURE_CLUSTER_TUNE.seedShort,
	seedLong: DEFAULT_FIGURE_CLUSTER_TUNE.seedLong,
	clusterGap: DEFAULT_FIGURE_CLUSTER_TUNE.clusterGap,
	imageMinPx: DEFAULT_FIGURE_CLUSTER_TUNE.imageMinPx,
	glyphMaxSide: DEFAULT_FIGURE_CLUSTER_TUNE.glyphMaxSide,
	glyphStroke: DEFAULT_FIGURE_CLUSTER_TUNE.glyphStroke,
	rasterScale: FIGURE_RASTER_SETTINGS.scale,
	rasterPadX: FIGURE_RASTER_SETTINGS.padPctX,
	rasterPadY: FIGURE_RASTER_SETTINGS.padPctY,
	rasterTrimMargin: FIGURE_RASTER_SETTINGS.trimMargin,
	rasterTrimReach: FIGURE_RASTER_SETTINGS.trimReach,
	nearMargin: DEFAULT_WORDING_TUNE.nearMargin,
	labelBand: DEFAULT_WORDING_TUNE.labelBand,
	labelBandRight: DEFAULT_WORDING_TUNE.labelBandRight,
	shortLabelWords: DEFAULT_WORDING_TUNE.shortLabelWords,
	wordGap: DEFAULT_WORDING_TUNE.wordGap,
	lineTolerance: DEFAULT_WORDING_TUNE.lineTolerance,
	maxCandidates: DEFAULT_WORDING_TUNE.maxCandidates,
	maxPages: 0,
	captionWords: CAPTION_WORD_LIST.join("\n"),
	prompt: DEFAULT_WORDING_INSTRUCTIONS,
	lineFormat: DEFAULT_WORDING_LINE,
	model: "lite",
	language: "en",
	shouldAskModel: false,
});

const finiteOr = (value: unknown, fallback: number): number =>
	typeof value === "number" && Number.isFinite(value) ? value : fallback;

export const normalisePlaygroundSettings = (value: unknown): AssetPlaygroundSettings => {
	const source = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
	const defaults = defaultPlaygroundSettings();
	const model = ASSET_PLAYGROUND_MODELS.find((item) => item === source.model) ?? defaults.model;
	return {
		hairline: finiteOr(source.hairline, defaults.hairline),
		seedShort: finiteOr(source.seedShort, defaults.seedShort),
		seedLong: finiteOr(source.seedLong, defaults.seedLong),
		clusterGap: finiteOr(source.clusterGap, defaults.clusterGap),
		imageMinPx: finiteOr(source.imageMinPx, defaults.imageMinPx),
		glyphMaxSide: finiteOr(source.glyphMaxSide, defaults.glyphMaxSide),
		glyphStroke: finiteOr(source.glyphStroke, defaults.glyphStroke),
		rasterScale: finiteOr(source.rasterScale, defaults.rasterScale),
		rasterPadX: finiteOr(source.rasterPadX, defaults.rasterPadX),
		rasterPadY: finiteOr(source.rasterPadY, defaults.rasterPadY),
		rasterTrimMargin: finiteOr(source.rasterTrimMargin, defaults.rasterTrimMargin),
		rasterTrimReach: finiteOr(source.rasterTrimReach, defaults.rasterTrimReach),
		nearMargin: finiteOr(source.nearMargin, defaults.nearMargin),
		labelBand: finiteOr(source.labelBand, defaults.labelBand),
		labelBandRight: finiteOr(source.labelBandRight, defaults.labelBandRight),
		shortLabelWords: finiteOr(source.shortLabelWords, defaults.shortLabelWords),
		wordGap: finiteOr(source.wordGap, defaults.wordGap),
		lineTolerance: finiteOr(source.lineTolerance, defaults.lineTolerance),
		maxCandidates: finiteOr(source.maxCandidates, defaults.maxCandidates),
		maxPages: finiteOr(source.maxPages, defaults.maxPages),
		captionWords: typeof source.captionWords === "string" ? source.captionWords : defaults.captionWords,
		prompt: typeof source.prompt === "string" ? source.prompt : defaults.prompt,
		lineFormat: typeof source.lineFormat === "string" ? source.lineFormat : defaults.lineFormat,
		model,
		language: source.language === "cy" ? "cy" : "en",
		shouldAskModel: source.shouldAskModel === true,
	};
};

export const clusterTuneOf = (settings: AssetPlaygroundSettings): FigureClusterTune => ({
	hairline: settings.hairline,
	seedShort: settings.seedShort,
	seedLong: settings.seedLong,
	clusterGap: settings.clusterGap,
	imageMinPx: settings.imageMinPx,
	nearMargin: settings.nearMargin,
	glyphMaxSide: settings.glyphMaxSide,
	glyphStroke: settings.glyphStroke,
});

export const wordingTuneOf = (settings: AssetPlaygroundSettings): WordingTune => ({
	nearMargin: settings.nearMargin,
	labelBand: settings.labelBand,
	labelBandRight: settings.labelBandRight,
	shortLabelWords: settings.shortLabelWords,
	wordGap: settings.wordGap,
	lineTolerance: settings.lineTolerance > 0 ? settings.lineTolerance : 0.1,
	maxCandidates: Math.max(0, Math.floor(settings.maxCandidates)),
	captionWords: settings.captionWords
		.split("\n")
		.map((word) => word.trim())
		.filter((word) => word.length > 0),
});

export const readPlaygroundDraft = (): AssetPlaygroundSettings => {
	try {
		const raw = localStorage.getItem(ASSET_PLAYGROUND_DRAFT_KEY);
		if (!raw) {
			return defaultPlaygroundSettings();
		}
		return normalisePlaygroundSettings(JSON.parse(raw));
	} catch {
		return defaultPlaygroundSettings();
	}
};

export const writePlaygroundDraft = (settings: AssetPlaygroundSettings): void => {
	localStorage.setItem(ASSET_PLAYGROUND_DRAFT_KEY, JSON.stringify(settings));
};

export const readPlaygroundVariations = (): NamedPlaygroundVariation[] => {
	try {
		const raw = localStorage.getItem(ASSET_PLAYGROUND_VARIATIONS_KEY);
		const parsed = raw ? (JSON.parse(raw) as unknown) : [];
		if (!Array.isArray(parsed)) {
			return [];
		}
		return parsed.flatMap((item) => {
			if (!item || typeof item !== "object") {
				return [];
			}
			const record = item as { name?: unknown; settings?: unknown };
			if (typeof record.name !== "string" || record.name.trim().length === 0) {
				return [];
			}
			return [{ name: record.name.trim(), settings: normalisePlaygroundSettings(record.settings) }];
		});
	} catch {
		return [];
	}
};

export const savePlaygroundVariation = (
	name: string,
	settings: AssetPlaygroundSettings,
): NamedPlaygroundVariation[] => {
	const trimmed = name.trim();
	const next = readPlaygroundVariations().filter((item) => item.name !== trimmed);
	next.push({ name: trimmed, settings: normalisePlaygroundSettings(settings) });
	localStorage.setItem(ASSET_PLAYGROUND_VARIATIONS_KEY, JSON.stringify(next));
	return next;
};

export const deletePlaygroundVariation = (name: string): NamedPlaygroundVariation[] => {
	const next = readPlaygroundVariations().filter((item) => item.name !== name);
	localStorage.setItem(ASSET_PLAYGROUND_VARIATIONS_KEY, JSON.stringify(next));
	return next;
};
